import { useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, ArrowRight, GitBranch, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useUserGroupCoverage, useUserGroupMemberships } from '@/hooks/useUserGroups';
import { useTenantUsers } from '@/hooks/useTenantUsers';
import type { RestrictionState, RestrictionStatus } from '@/services/userGroup.service';
import { CatalogStateBadge } from './ScopeEditors';

const STATUS_LABEL: Record<RestrictionStatus, string> = {
  applied: 'Restricted',
  skipped: 'Not connected — shown',
  denied: 'Not connected — hidden',
  not_applicable: '—',
  absent: 'Not in this branch',
};

const STATE_LABEL: Partial<Record<RestrictionState, { label: string; variant: 'secondary' | 'destructive' | 'outline' }>> = {
  breaks_on_publish: { label: 'Breaks at next publish', variant: 'destructive' },
  broken: { label: 'Broken', variant: 'destructive' },
};

function StatusBadge({ status }: { status: RestrictionStatus | undefined }) {
  if (!status || status === 'not_applicable' || status === 'absent') {
    return <span className="text-muted-foreground">{status === 'absent' ? STATUS_LABEL.absent : '—'}</span>;
  }
  const variant = status === 'applied' ? 'default' : status === 'denied' ? 'destructive' : 'secondary';
  return <Badge variant={variant}>{STATUS_LABEL[status]}</Badge>;
}

/** Production's status, and Develop's after an arrow when the next publish changes it. */
function StatusCell({ now, after }: { now: RestrictionStatus | undefined; after: RestrictionStatus | undefined }) {
  const same = after === undefined || after === now;
  if (same) return <StatusBadge status={now} />;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <StatusBadge status={now} />
      <ArrowRight className="h-3 w-3 text-muted-foreground" />
      <StatusBadge status={after} />
    </span>
  );
}

/**
 * Read-only view of what each group's restrictions do to each metric, computed
 * from the metrics' configured join trees — the same thing report builds use.
 * Shows Production, and where unpublished Develop changes would change a status,
 * the status after the next publish too.
 */
export default function CoverageTab() {
  const { data, isLoading } = useUserGroupCoverage();
  const { data: users } = useTenantUsers();
  const { data: memberships } = useUserGroupMemberships();

  // Restrictions apply to Admins' and Builders' charts, dashboards and Ellie, but
  // those roles can still read raw data and change the model, so they aren't
  // reliably bound by them. Frontegg holds roles, so this is worked out here.
  const elevatedMembers = useMemo(() => {
    if (!data || !users || !memberships) return [];
    const scoped = new Map(data.groups
      .filter((g) => g.restrictions.length > 0 || g.hidden_metric_ids.length > 0)
      .map((g) => [g.id, g.name]));
    return users
      .filter((u) => scoped.has(memberships[u.id] ?? '')
        && (u.roles ?? []).some((r) => ['admin', 'builder'].includes((r.key ?? '').toLowerCase())))
      .map((u) => ({ user: u.name || u.email, group: scoped.get(memberships[u.id])! }));
  }, [data, users, memberships]);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const group = useMemo(() => {
    if (!data?.groups.length) return null;
    return data.groups.find((g) => g.id === groupId) ?? data.groups[0];
  }, [data, groupId]);

  const metrics = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.metrics ?? []).filter((m) => !q || m.name.toLowerCase().includes(q));
  }, [data, search]);

  // Develop always exists once a tenant has used it, so the note is about whether
  // the next publish actually changes anything shown here.
  const publishChangesSomething = useMemo(() => !!data && (
    data.metrics.some((m) => m.state !== 'published')
    || data.groups.some((g) => g.restrictions.some((r) => r.state !== 'live' || (r.after_publish !== null
      && Object.entries(r.statuses).some(([id, now]) => {
        const after = r.after_publish![id];
        return after !== now;
      }))))
  ), [data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data?.has_draft && publishChangesSomething && (
        <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
          <GitBranch className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Develop has unpublished changes that affect access. Statuses are for Production; where the next publish changes one, it's
            shown as <span className="whitespace-nowrap">now → after publish</span>.
          </span>
        </div>
      )}
      {elevatedMembers.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <span>
            {elevatedMembers.map((m) => `${m.user} (${m.group})`).join(', ')}{' '}
            {elevatedMembers.length === 1 ? 'is an Admin or Builder' : 'are Admins or Builders'} in a restricted group.
            Restrictions apply to their charts, dashboards and Ellie, but those roles can still read raw data and change
            the data model, so they don't fully limit what {elevatedMembers.length === 1 ? 'this person' : 'these people'}{' '}
            can see. Use the ReadOnly role for anyone who must be restricted.
          </span>
        </div>
      )}
      {(data?.warnings ?? []).length > 0 && (
        <div className="space-y-2">
          {data!.warnings.map((w, idx) => (
            <div
              key={idx}
              className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
                w.level === 'error' ? 'border-destructive/50 bg-destructive/10' : 'border-amber-500/50 bg-amber-500/10'
              }`}
            >
              {w.level === 'error'
                ? <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                : <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />}
              <span>{w.message}</span>
            </div>
          ))}
        </div>
      )}

      {!group ? (
        <p className="text-sm text-muted-foreground">No groups yet. Create one on the Groups tab.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Select value={group.id} onValueChange={setGroupId}>
              <SelectTrigger className="w-[260px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {data!.groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name} ({g.member_count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Search metrics..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
          </div>
          <div className="border rounded-md max-h-[600px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metric</TableHead>
                  <TableHead>Visible</TableHead>
                  {group.restrictions.map((r, idx) => (
                    <TableHead key={idx}>
                      {r.description}
                      {STATE_LABEL[r.state] && (
                        <Badge variant={STATE_LABEL[r.state]!.variant} className="ml-2 text-[10px] font-normal">
                          {STATE_LABEL[r.state]!.label}
                        </Badge>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {metrics.map((m) => {
                  const hidden = group.hidden_metric_ids.includes(m.id);
                  return (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">
                        {m.name}
                        <CatalogStateBadge state={m.state} />
                      </TableCell>
                      <TableCell>
                        {hidden ? <Badge variant="destructive">Hidden</Badge> : <span className="text-muted-foreground">Yes</span>}
                      </TableCell>
                      {group.restrictions.map((r, idx) => (
                        <TableCell key={idx}>
                          {hidden
                            ? <span className="text-muted-foreground">—</span>
                            : <StatusCell now={r.statuses[m.id]} after={r.after_publish?.[m.id]} />}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
